import { useState, useEffect, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, ShieldCheck } from 'lucide-react'
import { useAuth } from '../../auth/AuthContext'
import { Avatar, Button, Card, Chip, Drawer, cn } from '../../design'
import {
  listMembers,
  createMember,
  updateMember,
  deactivateMember,
  MEMBER_ROLES,
  MIN_PASSWORD_LENGTH,
  type Member,
  type MemberRole,
} from '../../api/members'

const MANAGER_ROLES: MemberRole[] = ['owner', 'admin']

const ROLE_TONE: Record<MemberRole, 'success' | 'info' | 'neutral' | 'warning'> = {
  owner: 'success',
  admin: 'info',
  member: 'neutral',
  viewer: 'warning',
}

const FORM_ID = 'new-member-form'

const inputCls =
  'w-full rounded-md border border-border bg-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent'

/**
 * Workspace member management.
 *
 * Mutations are gated server-side on workspace_members.role; the UI mirrors that
 * so non-managers see a read-only roster rather than buttons that 403.
 */
export function TeamMembersPane() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)

  const membersQ = useQuery({
    queryKey: ['members'],
    queryFn: listMembers,
    staleTime: 30_000,
  })

  const members = membersQ.data ?? []
  const canManage = !!user && MANAGER_ROLES.includes(user.role as MemberRole)
  const isOwner = user?.role === 'owner'
  const activeOwners = members.filter(m => m.role === 'owner' && m.is_active).length

  const roleMut = useMutation({
    mutationFn: ({ id, role }: { id: number; role: MemberRole }) => updateMember(id, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  })
  const activeMut = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      active ? updateMember(id, { is_active: 1 }) : deactivateMember(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members'] })
      qc.invalidateQueries({ queryKey: ['lookups', 'users'] })
    },
  })

  const mutError = roleMut.error ?? activeMut.error

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Members</h3>
          <p className="text-xs text-fg-subtle">
            {canManage
              ? 'Add people to the workspace and manage their access.'
              : 'Only owners and admins can add or change members.'}
          </p>
        </div>
        {canManage && (
          <Button size="sm" leftIcon={<Plus size={14} />} onClick={() => setCreating(true)}>
            Add member
          </Button>
        )}
      </div>

      {mutError && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
          {(mutError as Error).message || 'That change was rejected.'}
        </div>
      )}

      <Card padded={false}>
        {membersQ.isLoading && (
          <div className="px-5 py-10 text-center text-sm text-fg-subtle">Loading members…</div>
        )}
        {membersQ.error && (
          <div className="px-5 py-10 text-center text-sm text-danger">Failed to load members.</div>
        )}
        <ul className="divide-y divide-border">
          {members.map(m => {
            const isSelf = m.id === user?.id
            // Mirrors the server guards: no self-edits, owner changes are
            // owner-only, and the last active owner is protected.
            const lastOwner = m.role === 'owner' && activeOwners <= 1
            const canEditRole =
              canManage && !isSelf && !lastOwner && (m.role === 'owner' ? isOwner : true)
            const canToggleActive =
              canManage && !isSelf && !(m.role === 'owner' && (!isOwner || lastOwner))

            return (
              <li
                key={m.id}
                className={cn('px-5 py-3 flex items-center gap-3', !m.is_active && 'opacity-55')}
              >
                <Avatar size="sm" name={m.name} color={m.avatar_color ?? '#9aa0a6'} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate flex items-center gap-2">
                    {m.name}
                    {isSelf && <span className="text-[10px] text-fg-subtle">(you)</span>}
                    {!m.is_active && <Chip size="sm" tone="neutral">deactivated</Chip>}
                  </div>
                  <div className="text-xs text-fg-subtle truncate">{m.email}</div>
                </div>

                <span className="text-[10px] font-mono text-fg-subtle whitespace-nowrap">
                  {m.last_login_at ? `seen ${fmtDate(m.last_login_at)}` : 'never signed in'}
                </span>

                {canEditRole ? (
                  <select
                    value={m.role}
                    disabled={roleMut.isPending}
                    onChange={e => roleMut.mutate({ id: m.id, role: e.target.value as MemberRole })}
                    className="rounded-md border border-border bg-bg-elevated text-xs px-2 py-1"
                  >
                    {MEMBER_ROLES.map(r => (
                      // Only an owner can grant owner.
                      <option key={r} value={r} disabled={r === 'owner' && !isOwner}>{r}</option>
                    ))}
                  </select>
                ) : (
                  <Chip size="sm" tone={ROLE_TONE[m.role]}>{m.role}</Chip>
                )}

                {canToggleActive && (
                  <button
                    onClick={() => activeMut.mutate({ id: m.id, active: !m.is_active })}
                    disabled={activeMut.isPending}
                    className={cn(
                      'text-xs px-2 py-1 rounded border transition-colors',
                      m.is_active
                        ? 'border-border text-fg-muted hover:text-danger hover:border-danger/40'
                        : 'border-border text-fg-muted hover:bg-bg-muted',
                    )}
                  >
                    {m.is_active ? 'Deactivate' : 'Reactivate'}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
        {!membersQ.isLoading && members.length === 0 && (
          <div className="px-5 py-10 text-center text-sm text-fg-subtle">No members found.</div>
        )}
      </Card>

      <p className="text-[11px] text-fg-subtle flex items-center gap-1.5">
        <ShieldCheck size={12} />
        Members are deactivated, never deleted — their comments and history stay intact.
        Deactivating also signs them out everywhere.
      </p>

      {canManage && (
        <NewMemberDrawer open={creating} onOpenChange={setCreating} callerIsOwner={isOwner} />
      )}
    </div>
  )
}

function NewMemberDrawer({
  open,
  onOpenChange,
  callerIsOwner,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  callerIsOwner: boolean
}) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<MemberRole>('member')

  const createMut = useMutation({
    mutationFn: () =>
      createMember({
        name: name.trim(),
        email: email.trim(),
        password,
        role,
      }),
    onSuccess: (m: Member) => {
      qc.invalidateQueries({ queryKey: ['members'] })
      qc.invalidateQueries({ queryKey: ['lookups', 'users'] })
      onOpenChange(false)
      return m
    },
  })

  useEffect(() => {
    if (!open) return
    setName('')
    setEmail('')
    setPassword('')
    setRole('member')
    createMut.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const passwordTooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH
  const canSubmit =
    name.trim().length > 0 &&
    emailValid &&
    password.length >= MIN_PASSWORD_LENGTH &&
    !createMut.isPending

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    createMut.mutate()
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      width="md"
      title="Add member"
      description="Creates the account and adds it to this workspace."
      footer={
        <div className="flex items-center justify-end gap-2">
          {createMut.isError && (
            <span className="mr-auto text-xs text-danger">
              {(createMut.error as Error).message || 'Could not add member.'}
            </span>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" form={FORM_ID} disabled={!canSubmit}>
            {createMut.isPending ? 'Adding…' : 'Add member'}
          </Button>
        </div>
      }
    >
      <form id={FORM_ID} onSubmit={onSubmit} className="space-y-4">
        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">
            Name<span className="text-danger"> *</span>
          </span>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            required
            autoFocus
            className={inputCls}
          />
        </label>

        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">
            Email<span className="text-danger"> *</span>
          </span>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            placeholder="name@company.com"
            className={inputCls}
          />
          {email.trim() !== '' && !emailValid && (
            <p className="mt-1 text-xs text-danger">Enter a valid email address.</p>
          )}
        </label>

        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">
            Initial password<span className="text-danger"> *</span>
          </span>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            className={inputCls}
          />
          <p className={cn('mt-1 text-xs', passwordTooShort ? 'text-danger' : 'text-fg-subtle')}>
            At least {MIN_PASSWORD_LENGTH} characters. Share it with them over a trusted
            channel and have them change it after signing in.
          </p>
        </label>

        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">Role</span>
          <select
            value={role}
            onChange={e => setRole(e.target.value as MemberRole)}
            className={inputCls}
          >
            {MEMBER_ROLES.map(r => (
              <option key={r} value={r} disabled={r === 'owner' && !callerIsOwner}>{r}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-fg-subtle">
            Owners and admins can manage members. Members and viewers cannot.
            {!callerIsOwner && ' Only an owner can create another owner.'}
          </p>
        </label>
      </form>
    </Drawer>
  )
}

function fmtDate(ts: string): string {
  return new Date(ts.replace(' ', 'T')).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
}
